// src/utils/csvExport.js — Utilitário DRY para export CSV (AI-friendly headers)

/** Escapa valor para CSV: trata vírgulas, aspas e quebras de linha */
function escapeCSV(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Converte array de headers + array de rows (objetos) em string CSV */
function arrayToCSV(headers, rows) {
  const headerLine = headers.map(h => escapeCSV(h.label)).join(',');
  const dataLines = rows.map(row =>
    headers.map(h => escapeCSV(row[h.key])).join(',')
  );
  return [headerLine, ...dataLines].join('\r\n');
}

/** Trigger download de CSV com UTF-8 BOM (compatível Excel) */
function downloadCSV(content, filename) {
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Formata JSONB checklist para texto legível (pipe-separated) */
function formatChecklist(checklist) {
  if (!checklist) return '';
  let items = checklist;
  if (typeof items === 'string') {
    try { items = JSON.parse(items); } catch { return items; }
  }
  if (!Array.isArray(items)) return '';
  return items.map(item => (typeof item === 'string' ? item : item.text || '')).filter(Boolean).join(' | ');
}

/** Formata restricted_times JSONB para texto legível */
function formatRestrictedTimes(times) {
  if (!times) return '';
  let parsed = times;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { return parsed; }
  }
  if (!Array.isArray(parsed)) return '';
  return parsed.join(' | ');
}

// ─── Export Functions ────────────────────────────────────────────

const CLIENT_HEADERS = [
  { key: 'id', label: 'Client ID (Internal)' },
  { key: 'name', label: 'Client Full Name' },
  { key: 'email', label: 'Email Address' },
  { key: 'phone', label: 'Phone Number' },
  { key: 'address', label: 'Street Address' },
  { key: 'city', label: 'City' },
  { key: 'postal_code', label: 'Postal Code' },
  { key: 'service_day', label: 'Regular Service Day' },
  { key: 'time_window', label: 'Preferred Time Window' },
  { key: 'service_duration', label: 'Service Duration (HH:MM format)' },
  { key: 'default_price', label: 'Default Service Price (NZD)' },
  { key: 'payment_method', label: 'Payment Method (cash/invoice/bank_transfer)' },
  { key: 'team', label: 'Assigned Team' },
  { key: 'invoice_description', label: 'Invoice Line Description' },
  { key: 'xero_contact_id', label: 'Xero Contact ID' },
  { key: 'notes', label: 'Internal Notes' },
  { key: 'default_checklist', label: 'Default Cleaning Checklist' },
  { key: 'restricted_times', label: 'Restricted Time Slots' },
  { key: 'password', label: 'Access Code' },
  { key: 'created_at', label: 'Date Created' },
];

export function exportClientsCSV(clients) {
  const rows = clients.map(c => ({
    ...c,
    default_price: c.default_price ? parseFloat(c.default_price).toFixed(2) : '',
    default_checklist: formatChecklist(c.default_checklist),
    restricted_times: formatRestrictedTimes(c.restricted_times),
    created_at: c.created_at ? new Date(c.created_at).toISOString().split('T')[0] : '',
  }));
  const csv = arrayToCSV(CLIENT_HEADERS, rows);
  const date = new Date().toISOString().split('T')[0];
  downloadCSV(csv, `its_so_clean_clients_${date}.csv`);
  return rows.length;
}

const EMPLOYEE_HEADERS = [
  { key: 'uid', label: 'Employee ID' },
  { key: 'displayName', label: 'Employee Full Name' },
  { key: 'email', label: 'Email Address' },
  { key: 'role', label: 'System Role (Admin/Manager/Employee)' },
  { key: 'hourlyRate', label: 'Hourly Rate (NZD)' },
  { key: 'deductionRate', label: 'Tax Deduction Rate' },
  { key: 'xero_employee_id', label: 'Xero Employee ID' },
  { key: 'xero_employee_name', label: 'Xero Employee Name' },
  { key: 'xero_hourly_rate', label: 'Xero Hourly Rate (NZD)' },
  { key: 'teams', label: 'Assigned Teams' },
  { key: 'workDays', label: 'Regular Work Days' },
];

export function exportEmployeesCSV(employees, customMembers) {
  const empRows = employees.map(e => ({
    uid: e.uid,
    displayName: e.displayName,
    email: e.email || '',
    role: e.isAdmin ? 'Admin' : e.isClientManager ? 'Manager' : 'Employee',
    hourlyRate: e.hourlyRate != null ? parseFloat(e.hourlyRate).toFixed(2) : '',
    deductionRate: e.deductionRate != null ? `${(parseFloat(e.deductionRate) * 100).toFixed(1)}%` : '',
    xero_employee_id: e.xero_employee_id || '',
    xero_employee_name: e.xero_employee_name || '',
    xero_hourly_rate: e.xero_hourly_rate != null ? parseFloat(e.xero_hourly_rate).toFixed(2) : '',
    teams: (e.teams || []).join(' | '),
    workDays: (e.workDays || []).join(' | '),
  }));

  const customRows = (customMembers || []).map(m => ({
    uid: m.id,
    displayName: m.name,
    email: '',
    role: 'External',
    hourlyRate: '',
    deductionRate: '',
    xero_employee_id: '',
    xero_employee_name: '',
    xero_hourly_rate: '',
    teams: (m.teams || []).join(' | '),
    workDays: (m.days || []).join(' | '),
  }));

  const allRows = [...empRows, ...customRows];
  const csv = arrayToCSV(EMPLOYEE_HEADERS, allRows);
  const date = new Date().toISOString().split('T')[0];
  downloadCSV(csv, `its_so_clean_employees_${date}.csv`);
  return allRows.length;
}

const TEAM_HEADERS = [
  { key: 'teamName', label: 'Team Name' },
  { key: 'dayOfWeek', label: 'Day of Week' },
  { key: 'members', label: 'Team Members' },
  { key: 'memberCount', label: 'Number of Members' },
];

export function exportTeamScheduleCSV(weeklyTeams, employees, customMembers) {
  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const TEAM_LABELS = { team1: 'Team 1', team2: 'Team 2', team3: 'Team 3' };

  // Build uid->name lookup
  const nameMap = {};
  (employees || []).forEach(e => { nameMap[e.uid] = e.displayName; });
  (customMembers || []).forEach(m => { nameMap[m.id] = m.name; });

  const resolveName = (uid) => {
    if (typeof uid === 'string' && uid.startsWith('custom:')) {
      return uid.replace('custom:', '');
    }
    return nameMap[uid] || uid;
  };

  const rows = [];
  for (const [teamKey, days] of Object.entries(weeklyTeams || {})) {
    for (const day of DAYS) {
      const memberUids = days[day] || [];
      if (memberUids.length === 0) continue;
      const memberNames = memberUids.map(resolveName);
      rows.push({
        teamName: TEAM_LABELS[teamKey] || teamKey,
        dayOfWeek: day,
        members: memberNames.join(' | '),
        memberCount: memberNames.length,
      });
    }
  }

  const csv = arrayToCSV(TEAM_HEADERS, rows);
  const date = new Date().toISOString().split('T')[0];
  downloadCSV(csv, `its_so_clean_team_schedule_${date}.csv`);
  return rows.length;
}

// ─── Jobs Export ─────────────────────────────────────────────────

const JOB_HEADERS = [
  { key: 'id', label: 'Job ID' },
  { key: 'clientName', label: 'Client Full Name' },
  { key: 'clientId', label: 'Client ID (Internal)' },
  { key: 'date', label: 'Scheduled Date (YYYY-MM-DD)' },
  { key: 'startTime', label: 'Start Time (HH:MM)' },
  { key: 'endTime', label: 'End Time (HH:MM)' },
  { key: 'team', label: 'Assigned Team (team1/team2/team3)' },
  { key: 'status', label: 'Job Status (scheduled/completed/cancelled)' },
  { key: 'recurrence', label: 'Recurrence Pattern (none/weekly/fortnightly/3weekly/4weekly/monthly)' },
  { key: 'seriesId', label: 'Recurring Series ID' },
  { key: 'invoiceAmount', label: 'Invoice Amount (NZD)' },
  { key: 'notes', label: 'Job Notes' },
  { key: 'checklist', label: 'Checklist Items' },
];

export function exportJobsCSV(jobs) {
  const rows = jobs.map(j => ({
    id: j.id || '',
    clientName: j.clientName || '',
    clientId: j.clientId || '',
    date: j.date || '',
    startTime: j.startTime || '',
    endTime: j.endTime || '',
    team: j.team || 'team1',
    status: j.status || 'scheduled',
    recurrence: j.recurrence || 'none',
    seriesId: j.seriesId || '',
    invoiceAmount: j.invoiceAmount ? parseFloat(j.invoiceAmount).toFixed(2) : '',
    notes: j.notes || '',
    checklist: formatChecklist(j.checklist),
  }));
  const csv = arrayToCSV(JOB_HEADERS, rows);
  const date = new Date().toISOString().split('T')[0];
  downloadCSV(csv, `its_so_clean_jobs_${date}.csv`);
  return rows.length;
}
